import axios from "axios";
import { clearLocalStorageSession } from "./authUtils";
// Development
const API_DOMAIN_DEV_URL = "https://be.dev.jaibharatconst.com";
const BASE_URL = process.env.REACT_APP_BE_URL || API_DOMAIN_DEV_URL;
export const BASE_BE_URL = `${BASE_URL}/api`;
const REFRESH_URL = "/auth/refresh"; // update if backend uses a different URL
const LOGOUT_URL = "/auth/signout"; // sample path
// LOGIN OTP ENDPOINTS
const SEND_OTP_URL = "/auth/otp/login";
const VERIFY_OTP_URL = "/auth/otp/verify";
const RESEND_OTP_URL = "/auth/otp/resend";
// VERIFY PHONE OTP
const VERIFY_CONTACT_SEND_OTP = "/auth/verify-contact/send-otp";
const VERIFY_CONTACT_VERIFY_OTP = "/auth/verify-contact/verify-otp"

/// Axios instance
const api = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    headers: {
        "Auth-Mode": "cookie",
    },
});

/* ---------------------------
   Refresh Token Handling
--------------------------- */
let isRefreshing = false;
let requestQueue = [];

const enqueue = (cb) => requestQueue.push(cb);

const resolveQueue = (ok) => {
    requestQueue.forEach((cb) => cb(ok));
    requestQueue = [];
};

export const refreshToken = async () => {
    try {
        await axios.post(
            BASE_URL + REFRESH_URL,
            {},
            {
                withCredentials: true,
                headers: {
                    "Auth-Mode": "cookie",
                },
            }
        );
        return true;
    } catch {
        return false;
    }
};

/* ---------------------------
   Axios Interceptors
--------------------------- */
api.interceptors.response.use(
    (res) => res,
    async (error) => {
        const originalRequest = error.config;

        if (!error.response || error.response.status !== 401) {
            return Promise.reject(error);
        }

        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                enqueue(async (success) => {
                    if (success) {
                        try {
                            resolve(await api(originalRequest));
                        } catch (err) {
                            reject(err);
                        }
                    } else {
                        // refresh failed → logout
                        clearLocalStorageSession();
                        window.location.href = "/login";
                        reject(error);
                    }
                });
            });
        }

        isRefreshing = true;
        const ok = await refreshToken();
        isRefreshing = false;
        resolveQueue(ok);

        if (ok) {
            originalRequest._retry = true;
            return api(originalRequest);
        } else {
            // refresh failed → logout
            clearLocalStorageSession();
            window.location.href = "/login";
            return Promise.reject(error);
        }
    }
);

/* ---------------------------
   OTP LOGIN — SEND OTP
--------------------------- */
export const sendOtp = async (phone) => {
    const payload = {
        emailOrPhone: String(phone),
    };

    const resp = await api.post(SEND_OTP_URL, payload);
    return resp.data;
};

/* ---------------------------
   OTP LOGIN — VERIFY OTP (creates session)
--------------------------- */
export const verifyOtp = async (phone, otp) => {
    const payload = {
        emailOrPhone: String(phone),
        otp: String(otp),
    };
    const resp = await api.post(VERIFY_OTP_URL, payload);
    return resp.data;
};

/* ---------------------------
   OTP LOGIN — RESEND SEND OTP
--------------------------- */
export const resendOtp = async (phone) => {
    const payload = {
        emailOrPhone: String(phone),
    };
    const resp = await api.post(RESEND_OTP_URL, payload);
    return resp.data;
};

/* ---------------------------
   LOGOUT
--------------------------- */
export const logoutApi = async () => {
    await api.post(LOGOUT_URL, {});
};

/* ---------------------------
   VERIFY PHONE — SEND OTP
--------------------------- */
export const verifyPhoneSendOtp = async (phone) => {
    const payload = {
        emailOrPhone: String(phone),
    };

    const resp = await api.post(VERIFY_CONTACT_SEND_OTP, payload);
    return resp.data;
};

/* ---------------------------
   VERIFY PHONE — VERIFY OTP
--------------------------- */
export const verifyPhoneOtp = async (phone, otp) => {
    const payload = {
        emailOrPhone: String(phone),
        otp: String(otp),
    };
    const resp = await api.post(VERIFY_CONTACT_VERIFY_OTP, payload);
    return resp.data;
};

/* ---------------------------
   Generic API Wrapper Methods
   ---------------------------
   - Handles GET, POST, PUT, PATCH, DELETE requests
   - Automatically attaches JSON headers
   - Detects FormData and applies multipart headers
--------------------------- */

// GET request (JSON header)
export const get = (url, config = {}) =>
    api.get(url, {
        ...config,
        headers: {
            "Content-Type": "application/json",
            ...(config.headers || {}),
        },
    });


// POST request
// - If FormData → send as multipart/form-data
// - Else → send as application/json
export const post = (url, data) => {
    if (data instanceof FormData) {
        return api.post(url, data, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }
    return api.post(url, data,
        {
            headers: {
                "Content-Type": "application/json",
            }
        }
    );
};

// PUT request (JSON body)
export const put = (url, data) => api.put(url, data, {
    headers: {
        "Content-Type": "application/json",
    }
});

// DELETE request (no body, only headers)
export const del = (url) =>
    api.delete(url, {
        headers: {
            "Content-Type": "application/json",
        },
    });

// PATCH request (JSON body)

export const patch = (url, data) => {
    if (data instanceof FormData) {
        return api.patch(url, data, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }
    return api.patch(url, data,
        {
            headers: {
                "Content-Type": "application/json",
            }
        }
    );
};

export default api;